/**
 * Guards a server-supplied colour string before it is concatenated into an inline
 * `style="…"` value (band swatches, matrix/SEG cell fills, PDF-safe styles).
 *
 * The template/PDF pipeline HTML-escapes attribute values but does NOT strip CSS
 * metacharacters (`;`, `}`, `url()`, …), so an unexpected backend value could inject
 * extra CSS declarations. Only well-formed hex (`#RGB`, `#RGBA`, `#RRGGBB`, `#RRGGBBAA`)
 * and `rgb()/rgba()` functional notation are allowed through; anything else returns ''
 * so the caller falls back to no colour rather than emitting attacker-controlled CSS.
 */
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_COLOR = /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d+)\s*)?\)$/;

export function sanitizeCssColor(value: string | null | undefined): string {
    if (!value) return '';
    const trimmed = value.trim();
    return HEX_COLOR.test(trimmed) || RGB_COLOR.test(trimmed) ? trimmed : '';
}

/**
 * Strict hex-only variant of {@link sanitizeCssColor} for colours the backend only ever stores
 * as hex (e.g. tenant Risk Level config). Returns '' for anything else, including rgb()/rgba().
 */
export function sanitizeHexColor(value: string | null | undefined): string {
    if (!value) return '';
    const trimmed = value.trim();
    return HEX_COLOR.test(trimmed) ? trimmed : '';
}

/**
 * Sanitised inline `background-color:… !important` declaration for a server-supplied colour
 * (PDF-safe; guards against CSS injection). Returns '' when the colour is missing or rejected
 * so callers emit no colour rather than attacker-controlled CSS.
 */
export function backgroundCssStyle(color: string | null | undefined): string {
    const safe = sanitizeCssColor(color);
    return safe ? `background-color:${safe} !important` : '';
}
